import ComponentList from "components/ComponentList";
import { ShowMoreBtn } from "components/List";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import apiRequest from "utils/apiRequestWithErrorCode";
import {
  Box,
  Button,
  debounce,
  IconButton,
  InputBase,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import styled from "styled-components";
import { Skeleton } from "@material-ui/lab";
import { byLastModified } from "utils/sort";
import Account from "./Item";
import { EmailWithSmimeCerts } from "@dashboard-v3/api";

const LIST_LIMIT = 50;

export default function Accounts() {
  const { t } = useTranslation("smime");
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<"ALL" | "MORE">();
  const [emails, setEmails] = useState<EmailWithSmimeCerts[]>();
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const searchTextRef = useRef("");

  useEffect(() => {
    async function initialFetch() {
      setLoading("ALL");
      await fetchEmails();
      setLoading(undefined);
    }

    initialFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchEmails(offset = 0, query?: string) {
    try {
      let url = `/smime/emails?offset=${offset}&limit=${LIST_LIMIT}`;
      if (query) {
        url += `&query=${encodeURIComponent(query)}`;
      }

      const res = await apiRequest<EmailWithSmimeCerts[]>("GET", url);

      setHasMore(res.length === LIST_LIMIT);
      setEmails(prev => {
        if (offset === 0) return res.sort(byLastModified);
        return (prev || []).concat(res).sort(byLastModified);
      });
    } catch (e) {
      enqueueSnackbar(t(`common:errors.fetchError`), {
        variant: "error",
      });
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (text: string) => {
      searchTextRef.current = text;
      setSearchOffset(0);
      setLoading("ALL");
      await fetchEmails(0, text || undefined);
      setLoading(undefined);
    }, 300),
    []
  );

  function handleSearchChange(value: string) {
    setSearchInput(value);
    debouncedSearch(value);
  }

  function handleClearSearch() {
    setSearchInput("");
    debouncedSearch("");
  }

  async function loadMore() {
    setLoading("MORE");
    const newOffset = searchOffset + LIST_LIMIT;
    await fetchEmails(newOffset, searchTextRef.current || undefined);
    setSearchOffset(newOffset);
    setLoading(undefined);
  }

  async function deleteItem(email: string) {
    try {
      setDeletingId(email);
      await apiRequest("DELETE", `/smime/emails/${email}`);

      setEmails(prev => {
        return prev?.filter(e => e.email !== email) || [];
      });

      enqueueSnackbar(t("accountDeleted"), {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(t(`common:errors.fetchError`), { variant: "error" });
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <SearchContainer>
        <InputBase
          fullWidth
          name="search"
          placeholder={t("accounts.search.placeholder")}
          value={searchInput}
          onChange={e => handleSearchChange(e.target.value)}
          startAdornment={
            searchInput ? (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ) : (
              <SearchIcon color="disabled" />
            )
          }
        />
      </SearchContainer>
      <ComponentList
        list={emails}
        loading={loading === "ALL"}
        emptyMsg={<EmptyList />}
        renderItems={item =>
          deletingId && deletingId === item.email ? (
            <Skeleton variant="rect" height={116} />
          ) : (
            <Account
              key={item.email}
              account={item}
              onDelete={() => deleteItem(item.email)}
            />
          )
        }
      />
      {hasMore && (
        <ShowMoreBtn loading={loading === "MORE"} onClick={loadMore} />
      )}
    </>
  );
}

const SearchContainer = styled.div`
  padding: 5px 10px;
  border-radius: 4px;
  background-color: rgb(224, 231, 239);
  margin-top: 14px;

  input {
    padding-left: 10px;
  }

  svg {
    font-size: 18px;
  }
`;

function EmptyList() {
  const { t } = useTranslation("smime");

  return (
    <Box p="10px">
      <Typography>{t("accounts.emptyMsg")}</Typography>
      <Box mt="20px">
        <Button
          variant="contained"
          color="primary"
          disableElevation
          href="/smime-certs/accounts/new"
        >
          {t("accounts.emptyAction")}
        </Button>
      </Box>
    </Box>
  );
}
